/**
 * <volume-tiers> — native volume discount tier table.
 *
 * Replaces the Vitals "Volume Discounts" product-page widget. Responsibilities:
 *   1. Highlight the tier the shopper currently qualifies for, live as they
 *      change the quantity input.
 *   2. Re-render tier prices when the selected variant changes.
 *
 * All money strings are rendered server-side by Liquid and looked up here by
 * variant id — this class never does currency math.
 */
class VolumeTiers extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('.volume-tiers__row'));
    if (!this.rows.length) return;

    try {
      this.data = JSON.parse(this.querySelector('.volume-tiers__data').textContent);
    } catch (e) {
      // Malformed metafield JSON — leave the server-rendered table as-is.
      return;
    }

    this.form = document.getElementById(this.dataset.formId);
    this.qtyInput = this.form
      ? this.form.querySelector('input[name="quantity"]')
      : null;

    // The theme's quantity-input lives outside the <form> and is associated by
    // the `form` attribute, so fall back to a document-wide lookup.
    if (!this.qtyInput) {
      this.qtyInput = document.querySelector(
        `input[name="quantity"][form="${this.dataset.formId}"], #quantity-${this.dataset.formId}`
      );
    }

    this.variantInput = this.form
      ? this.form.querySelector('input[name="id"], select[name="id"]')
      : null;

    this.onQtyChange = this.onQtyChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);

    if (this.qtyInput) {
      // `input` catches typing, `change` catches the +/- buttons.
      this.qtyInput.addEventListener('input', this.onQtyChange);
      this.qtyInput.addEventListener('change', this.onQtyChange);
    }

    if (this.variantInput) {
      this.variantInput.addEventListener('change', this.onVariantChange);
      // The variant picker sets `.value` programmatically, which fires no event.
      this.watchVariant();
    }

    this.onQtyChange();
  }

  disconnectedCallback() {
    if (this.qtyInput) {
      this.qtyInput.removeEventListener('input', this.onQtyChange);
      this.qtyInput.removeEventListener('change', this.onQtyChange);
    }
    if (this.variantInput) {
      this.variantInput.removeEventListener('change', this.onVariantChange);
    }
    if (this.observer) this.observer.disconnect();
  }

  /** Poll-free detection of programmatic variant changes. */
  watchVariant() {
    this.lastVariant = this.variantInput.value;
    this.observer = new MutationObserver(() => {
      if (this.variantInput.value !== this.lastVariant) {
        this.lastVariant = this.variantInput.value;
        this.onVariantChange();
      }
    });
    this.observer.observe(this.variantInput, {
      attributes: true,
      attributeFilter: ['value'],
    });
  }

  currentQty() {
    const raw = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
    return Number.isNaN(raw) || raw < 1 ? 1 : raw;
  }

  /** Highlight the highest tier the current quantity qualifies for. */
  onQtyChange() {
    const qty = this.currentQty();
    let active = null;

    this.rows.forEach((row) => {
      if (qty >= parseInt(row.dataset.qty, 10)) active = row;
    });

    this.rows.forEach((row) => {
      const isActive = row === active;
      row.classList.toggle('volume-tiers__row--active', isActive);
      if (isActive) {
        row.setAttribute('aria-current', 'true');
      } else {
        row.removeAttribute('aria-current');
      }
    });
  }

  /** Swap in the selected variant's pre-formatted tier prices. */
  onVariantChange() {
    const tiers = this.data[this.variantInput.value];
    if (!tiers) return;

    this.rows.forEach((row, i) => {
      const tier = tiers[i];
      if (!tier) return;

      const unit = row.querySelector('.js-unit-price');
      const save = row.querySelector('.js-saving');
      if (unit) unit.textContent = tier.unit;
      if (save) save.textContent = tier.save;
    });
  }
}

if (!customElements.get('volume-tiers')) {
  customElements.define('volume-tiers', VolumeTiers);
}
